import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { 
  Baby, ShieldCheck, Heart, Sparkle, Warning, CheckCircle, Clock, XCircle, ArrowRight
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CLAIM_ICONS = {
  safe_for_babies: Baby, safe_for_children: Baby, safe_for_teens: Baby,
  safe_for_adults: ShieldCheck, safe_during_pregnancy: Heart, safe_during_lactation: Heart,
  safe_for_sensitive: Sparkle, safe_for_fragile: Sparkle, safe_for_daily_use: ShieldCheck,
};

const CLAIM_GROUPS = [
  { label: 'Population / Age', ids: ['safe_for_babies', 'safe_for_children', 'safe_for_teens', 'safe_for_adults'] },
  { label: 'Special Conditions', ids: ['safe_during_pregnancy', 'safe_during_lactation'] },
  { label: 'Skin Type', ids: ['safe_for_sensitive', 'safe_for_fragile'] },
  { label: 'Usage', ids: ['safe_for_daily_use'] },
];

export function ClaimSelectionSection({ safetyClaims, setSafetyClaims, hasClaims, setHasClaims }) {
  const [claimsData, setClaimsData] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/safety-claims`).then(r => setClaimsData(r.data)).catch(() => {});
  }, []);

  const toggleClaim = (claimId) => {
    setSafetyClaims(prev =>
      prev.includes(claimId) ? prev.filter(c => c !== claimId) : [...prev, claimId]
    );
  };

  return (
    <div className="card-flat p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-[#111827]">Safety & Population Claims</h2>
          <p className="text-sm text-[#4B5563]">Do you intend to make a safety or population-based claim?</p>
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="claims-toggle" className="text-sm text-[#4B5563]">{hasClaims ? 'Yes' : 'No'}</Label>
          <Switch id="claims-toggle" checked={hasClaims} onCheckedChange={setHasClaims} data-testid="claims-toggle" />
        </div>
      </div>

      {hasClaims && claimsData && (
        <div className="space-y-5 mt-2">
          {CLAIM_GROUPS.map(group => {
            const available = group.ids.filter(id => claimsData.claims[id]);
            if (available.length === 0) return null;
            return (
              <div key={group.label}>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#4B5563] mb-2">{group.label}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {available.map(claimId => {
                    const claim = claimsData.claims[claimId];
                    const Icon = CLAIM_ICONS[claimId] || ShieldCheck;
                    const selected = safetyClaims.includes(claimId);
                    const pop = claimsData.populations[claim.population];
                    return (
                      <button key={claimId} type="button" onClick={() => toggleClaim(claimId)}
                        className={`text-left p-3 border-2 rounded-sm transition-all ${
                          selected ? 'border-[#0B2447] bg-[#0B2447]/5' : 'border-gray-200 hover:border-gray-300 bg-white'
                        }`}
                        data-testid={`claim-${claimId}`}>
                        <div className="flex items-center gap-2">
                          <Icon size={18} weight={selected ? 'duotone' : 'regular'} className={selected ? 'text-[#0B2447]' : 'text-[#4B5563]'} />
                          <span className={`text-sm font-semibold ${selected ? 'text-[#0B2447]' : 'text-[#111827]'}`}>{claim.label}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[10px] text-[#4B5563] font-mono">{pop?.body_weight} kg · {pop?.age_range}</span>
                          {claim.escalation && <span className="text-[10px] text-[#F05252] font-semibold">Aᴳ REVIEW</span>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {safetyClaims.length > 0 && (
            <div className="p-3 bg-[#F9FAFB] border border-gray-200 rounded-sm">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#4B5563] mb-1">Selected Claims</p>
              <div className="flex flex-wrap gap-1.5">
                {safetyClaims.map(id => (
                  <span key={id} className="text-xs font-semibold px-2 py-0.5 bg-[#0B2447] text-white rounded-sm">
                    {claimsData.claims[id]?.label || id}
                  </span>
                ))}
              </div>
              <p className="text-xs text-[#4B5563] mt-2">
                Exposure assumptions will use the most conservative population parameters.
                {safetyClaims.some(c => claimsData.claims[c]?.escalation) && (
                  <span className="text-[#F05252] font-semibold"> Aᴳ expert review may be triggered.</span>
                )}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ============== CLAIM RESULTS DISPLAY ============== */

function ClaimStatusBadge({ status }) {
  const cfg = {
    provisionally_supportable:    { icon: CheckCircle, cls: 'status-acceptable', label: 'Provisionally Supportable' },
    supportable_with_limitations: { icon: Clock, cls: 'status-needs-review', label: 'Supportable with Limitations' },
    requires_review:              { icon: Warning, cls: 'status-needs-review', label: 'Requires Aᴳ Review' },
    not_supportable:              { icon: XCircle, cls: 'status-not-acceptable', label: 'Not Currently Supportable' },
  };
  const c = cfg[status] || cfg.requires_review;
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-sm ${c.cls}`}>
      <Icon size={14} weight="bold" /> {c.label}
    </span>
  );
}

export function ClaimResultsSection({ claimEvaluations, exposureAssumptions }) {
  if (!claimEvaluations || claimEvaluations.length === 0) return null;

  return (
    <div className="card-flat overflow-hidden mb-8" data-testid="claim-results-section">
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-[#111827]">Claim Support Review</h2>
        <p className="text-sm text-[#4B5563]">Assessment of safety claims based on formulation data</p>
      </div>

      {/* Exposure Assumptions Used */}
      {exposureAssumptions && (
        <div className="p-4 border-b border-gray-200 bg-[#F9FAFB]">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#4B5563] mb-2">
            Exposure Assumptions Used {exposureAssumptions.claim_driven && '(Claim-Driven)'}
            {exposureAssumptions.is_custom_override && ' (Custom Override)'}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div>
              <span className="text-[#4B5563]">Population:</span>{' '}
              <span className="font-semibold">{exposureAssumptions.population_label || exposureAssumptions.population}</span>
            </div>
            <div>
              <span className="text-[#4B5563]">Body Weight:</span>{' '}
              <span className="font-mono font-semibold">{exposureAssumptions.body_weight} kg</span>
            </div>
            <div>
              <span className="text-[#4B5563]">Amount:</span>{' '}
              <span className="font-mono font-semibold">{exposureAssumptions.amount_g} g/use</span>
            </div>
            <div>
              <span className="text-[#4B5563]">Conservatism:</span>{' '}
              <span className={`font-semibold ${exposureAssumptions.conservatism === 'highest' ? 'text-[#9B1C1C]' : exposureAssumptions.conservatism === 'high' ? 'text-[#723B13]' : 'text-[#4B5563]'}`}>
                {exposureAssumptions.conservatism?.charAt(0).toUpperCase() + exposureAssumptions.conservatism?.slice(1)}
              </span>
            </div>
            <div>
              <span className="text-[#4B5563]">Route:</span>{' '}
              <span className="font-semibold capitalize">{exposureAssumptions.exposure_route}</span>
            </div>
            <div>
              <span className="text-[#4B5563]">Frequency:</span>{' '}
              <span className="font-mono font-semibold">{exposureAssumptions.frequency}x/day</span>
            </div>
            <div>
              <span className="text-[#4B5563]">Retention:</span>{' '}
              <span className="font-mono font-semibold">{exposureAssumptions.retention_factor}</span>
            </div>
            {exposureAssumptions.overlays?.length > 0 && (
              <div>
                <span className="text-[#4B5563]">Overlays:</span>{' '}
                <span className="font-semibold">{exposureAssumptions.overlays.join(', ')}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Claim Evaluations */}
      <div className="divide-y divide-gray-200">
        {claimEvaluations.map(evaluation => (
          <div key={evaluation.claim_id} className="p-4 hover:bg-gray-50" data-testid={`claim-eval-${evaluation.claim_id}`}>
            <div className="flex items-start justify-between gap-4 mb-2">
              <div>
                <h3 className="font-semibold text-[#111827]">{evaluation.claim_label}</h3>
                <p className="text-xs text-[#4B5563]">Population: {evaluation.population}</p>
              </div>
              <ClaimStatusBadge status={evaluation.status} />
            </div>
            <p className="text-sm text-[#4B5563] mb-2">{evaluation.justification}</p>
            {evaluation.issues && evaluation.issues.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {evaluation.issues.map(issue => (
                  <span key={issue} className="text-xs px-2 py-0.5 bg-[#FDE8E8] text-[#9B1C1C] rounded-sm font-medium">{issue}</span>
                ))}
              </div>
            )}
            {evaluation.escalation_required && (
              <div className="mt-2 flex items-center gap-1.5 text-xs text-[#F05252] font-semibold">
                <ArrowRight size={12} /> Escalation to Aᴳ review recommended
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Disclaimers */}
      <div className="p-4 bg-[#F3F4F6] border-t border-gray-200">
        <p className="text-xs text-[#4B5563]">
          This assessment is based on ingredient-level and exposure-based evaluation and does not replace clinical or claim substantiation testing. Population-specific claims may require additional HRIPT/RIPT, clinical, pediatric, or physician-supervised validation.
        </p>
      </div>
    </div>
  );
}
